@layer refine.base {
  :host {
    --_position: var(--toast-position, fixed);
    --_inset-top: var(--toast-inset-top, auto);
    --_inset-bottom: var(--toast-inset-bottom, 1rem);
    --_inset-right: var(--toast-inset-right, 1rem);
    --_inset-left: var(--toast-inset-left, auto);
    --_z-index: var(--toast-z-index, var(--z-toast));
    --_max-width: var(--toast-max-width, 400px);
    --_gap: var(--toast-gap, 0.5rem);

    position: var(--_position);
    inset: var(--_inset-top) var(--_inset-right) var(--_inset-bottom) var(--_inset-left);
    z-index: var(--_z-index);
    display: flex;
    flex-direction: column;
    gap: var(--_gap);
    max-width: var(--_max-width);
    pointer-events: none;
  }

  /* ── Position variants ────────────────────────────────────────────────── */

  :host([position='top-left']) {
    --_inset-top: 1rem;
    --_inset-bottom: auto;
    --_inset-left: 1rem;
    --_inset-right: auto;
  }

  :host([position='top-center']) {
    --_inset-top: 1rem;
    --_inset-bottom: auto;
    --_inset-left: 50%;
    --_inset-right: auto;

    transform: translateX(-50%);
  }

  :host([position='top-right']) {
    --_inset-top: 1rem;
    --_inset-bottom: auto;
    --_inset-right: 1rem;
    --_inset-left: auto;
  }

  :host([position='bottom-left']) {
    --_inset-bottom: 1rem;
    --_inset-left: 1rem;
    --_inset-right: auto;
    --_inset-top: auto;
  }

  :host([position='bottom-center']) {
    --_inset-bottom: 1rem;
    --_inset-left: 50%;
    --_inset-right: auto;
    --_inset-top: auto;

    transform: translateX(-50%);
  }

  :host([position='bottom-right']) {
    --_inset-bottom: 1rem;
    --_inset-right: 1rem;
    --_inset-left: auto;
    --_inset-top: auto;
  }

  /* ── Container ────────────────────────────────────────────────────────── */

  .toast-container {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
    row-gap: 0;
    perspective: 1200px;
    perspective-origin: center center;
    transition: row-gap var(--transition-normal);
  }

  /* ARIA live regions are layout-transparent. */
  .toast-live-region {
    display: contents;
  }

  :host(.hovered) .toast-container {
    row-gap: var(--_gap);
  }

  /* ── Wrapper (layout / stacking only — no animation) ─────────────────── */

  /* .toast-wrapper owns: grid position, z-index, stacking transforms.       */

  /* It never has opacity or transition set on it directly.                  */

  /* This keeps stacking rules and animation rules on separate elements,     */

  /* eliminating all specificity conflicts and !important usage.             */

  :host(:not(.hovered)) .toast-wrapper {
    grid-row: 1;
    grid-column: 1;
    align-self: end;
  }

  :host([position^='top']:not(.hovered)) .toast-wrapper {
    align-self: start;
  }

  .toast-wrapper {
    position: relative;
    pointer-events: auto;
    touch-action: pan-y;
  }

  /* Stacking (collapsed) — 3D perspective stack */
  :host(:not(.hovered)) .toast-wrapper:nth-last-child(1) {
    z-index: 3;
    transform: translateY(0) scale(1);
  }

  :host(:not(.hovered)) .toast-wrapper:nth-last-child(2) {
    z-index: 2;
    pointer-events: none;
    transform: translateY(-8px) scale(0.95);
  }

  :host(:not(.hovered)) .toast-wrapper:nth-last-child(3) {
    z-index: 1;
    pointer-events: none;
    transform: translateY(-14px) scale(0.9);
  }

  :host(:not(.hovered)) .toast-wrapper:nth-last-child(n + 4) {
    visibility: hidden;
    pointer-events: none;
  }

  /* Top positions: reverse stacking direction */
  :host([position^='top']) .toast-wrapper {
    transform-origin: center top;
  }

  :host([position^='top']:not(.hovered)) .toast-wrapper:nth-last-child(2) {
    transform: translateY(8px) scale(0.95);
  }

  :host([position^='top']:not(.hovered)) .toast-wrapper:nth-last-child(3) {
    transform: translateY(14px) scale(0.9);
  }

  /* Expanded (hovered) — stacking transforms are released per wrapper */
  :host(.hovered) .toast-wrapper {
    z-index: auto;
    visibility: visible;
    grid-row: auto;
    grid-column: 1;
    align-self: stretch;
    pointer-events: auto;
    transform: none;
  }

  /* Staggered expand delays (applied to wrapper transform only) */
  :host(.hovered) .toast-wrapper:nth-last-child(2) {
    transition-delay: var(--duration-75);
  }

  :host(.hovered) .toast-wrapper:nth-last-child(3) {
    transition-delay: var(--duration-100);
  }

  :host(.hovered) .toast-wrapper:nth-last-child(n + 4) {
    transition-delay: var(--duration-150);
  }

  /* ── Inner (animation target only — no layout role) ──────────────────── */

  /* .toast-inner owns: opacity, entry/exit transitions.                    */

  /* It never participates in stacking (that's the wrapper's job).          */

  /* Swipe gesture also animates .toast-inner to keep wrapper state clean.  */

  .toast-inner {
    position: relative;
    opacity: 1;
    transition: opacity var(--toast-exit-duration, 300ms) var(--ease-out);
  }

  /* Entry: snap to invisible with no transition, then rely on class removal to animate in */
  .toast-inner.entering {
    opacity: 0;
    transition: none;
  }

  /* Exit: fade out — wrapper stays in-flow so grid doesn't snap shut prematurely */
  .toast-inner.exiting {
    pointer-events: none;
    opacity: 0;
  }

  /* ── Alert inside toast ───────────────────────────────────────────────── */

  ::slotted(ore-alert),
  ore-alert {
    --_motion-animation: none;
    width: 100%;
    margin: 0;
    box-shadow: var(--shadow-lg);
  }

  /* ── Action buttons ───────────────────────────────────────────────────── */

  .toast-actions {
    display: flex;
    gap: var(--size-4);
    justify-content: flex-end;
  }

  /* ── Responsive: small screens ────────────────────────────────────────── */

  @media (width <= 480px) {
    :host {
      --_max-width: calc(100vw - var(--size-4));
      --_inset-right: var(--size-2);
      --_inset-left: var(--size-2);
    }

    :host([position='top-center']),
    :host([position='bottom-center']) {
      --_inset-left: var(--size-2);

      inset-inline-start: var(--size-2);
      transform: none;
    }
  }
}
